import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import NavigationMenu from '../components/NavigationMenu';
import ProjectsHeader from '../components/ProjectsHeader';
import MainContentArea from '../components/MainContentArea';
import StaleEmbeddingBanner from '../components/StaleEmbeddingBanner';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useNotifications } from '../contexts/NotificationsContext';
import { useAuth } from '../contexts/AuthContext';
import { buildMainNavLinks } from '../utils/mainNavLinks';
import SessionListItem from '../components/SessionListItem';
import './SessionsLayout.css';

const PAGE_SIZE = 25;

const SessionsLayout = () => {
  const { projectId, sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isSummaryRoute = /\/p\/[^/]+\/summary\/?$/.test(location.pathname);
  const { totalCount: horizonBadge, reviewCount, embeddingCount, schedulerErrorCount } = useNotifications();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const getInitialOpen = () => {
    try {
      const match = document.cookie.match(/(?:^|; )nav_open=([^;]*)/);
      if (match) {
        const v = decodeURIComponent(match[1]);
        return v === '1' || v === 'true';
      }
    } catch (_) {}
    return false;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(getInitialOpen);
  const [disableAnim, setDisableAnim] = useState(true);

  const [projectName, setProjectName] = useState('');
  const [projectCanEdit, setProjectCanEdit] = useState(false);
  const [activeSessionName, setActiveSessionName] = useState('');
  const [showArchivedChats, setShowArchivedChats] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setProjectName('');
      setProjectCanEdit(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/get`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ projectId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!cancelled && resp.ok && json?.success && json.data) {
          setProjectName(json.data.name || '');
          setProjectCanEdit(json.data.can_edit === true || json.data.is_owner !== false);
        }
      } catch (_) {
        if (!cancelled) {
          setProjectName('');
          setProjectCanEdit(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!sessionId) {
      setActiveSessionName('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/get`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ sessionId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!cancelled && resp.ok && json?.success && json.data?.name) {
          setActiveSessionName(json.data.name);
        }
      } catch (_) {
        if (!cancelled) setActiveSessionName('');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const headerTitle = useMemo(() => {
    if (isSummaryRoute) {
      return projectName ? `Summary · ${projectName}` : 'Project summary';
    }
    if (sessionId && activeSessionName) {
      return activeSessionName;
    }
    return projectName ? projectName : 'Chat';
  }, [isSummaryRoute, sessionId, activeSessionName, projectName]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setDisableAnim(false));
    return () => cancelAnimationFrame(id);
  }, []);

  // Redirect to /projects if no projectId
  useEffect(() => {
    if (!projectId) {
      navigate('/projects', { replace: true });
    }
  }, [projectId, navigate]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const fetchSessions = useCallback(async (p = 1) => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const body = { projectId: projectId, page: p, limit: PAGE_SIZE, includeArchived: !!showArchivedChats };
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/list`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load sessions');
      setSessions(json.data.items || []);
      setTotal(json.data.total || 0);
    } catch (e) {
      setError(e.message || 'Failed to load sessions');
      setSessions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [projectId, showArchivedChats]);

  useEffect(() => { setPage(1); }, [projectId, showArchivedChats]);
  useEffect(() => { fetchSessions(page); }, [fetchSessions, page]);

  // Listen for newly created sessions to add them immediately to the nav list
  useEffect(() => {
    const onCreated = (ev) => {
      const d = ev?.detail || {};
      if (!d?.session_id) return;
      // match current project
      if (projectId && d.project_id && String(d.project_id) !== String(projectId)) return;
      setSessions((prev) => {
        // avoid duplicates
        if ((prev || []).some(s => s.session_id === d.session_id)) return prev;
        const nextItem = {
          session_id: d.session_id,
          name: d.name || 'Untitled Session',
          created_at: d.created_at || new Date().toISOString(),
          modified_at: d.modified_at || d.created_at || new Date().toISOString(),
        };
        const combined = [nextItem, ...(prev || [])];
        return combined.slice(0, PAGE_SIZE);
      });
      setTotal((t) => (t || 0) + 1);
      // Ensure the new session is visible
      setPage(1);
    };
    window.addEventListener('cascade:session-created', onCreated);
    return () => window.removeEventListener('cascade:session-created', onCreated);
  }, [projectId]);

  const handleSessionRenamed = useCallback((renamedSessionId, name) => {
    setSessions((prev) =>
      (prev || []).map((s) =>
        s.session_id === renamedSessionId ? { ...s, name } : s
      )
    );
    if (sessionId && String(sessionId) === String(renamedSessionId)) {
      setActiveSessionName(name);
    }
  }, [sessionId]);

  useEffect(() => {
    const onRenamed = (ev) => {
      const d = ev?.detail || {};
      if (!d.session_id || !d.name) return;
      if (projectId && d.project_id && String(d.project_id) !== String(projectId)) return;
      handleSessionRenamed(d.session_id, d.name);
    };
    window.addEventListener('cascade:session-renamed', onRenamed);
    return () => window.removeEventListener('cascade:session-renamed', onRenamed);
  }, [projectId, handleSessionRenamed]);

  const extraContent = (
    <div>
      <div className="section-title" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span>Sessions</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 12, fontWeight: 400, color: '#6b7280' }}>
          <input
            type="checkbox"
            checked={showArchivedChats}
            onChange={(e) => setShowArchivedChats(e.target.checked)}
          />
          Show archived chats
        </label>
      </div>
      {loading && sessions.length === 0 && (
        <div style={{ padding: '12px', color: '#6b7280' }}><i className="fas fa-spinner fa-pulse" /> Loading…</div>
      )}
      {error && (
        <div style={{ padding: '12px', color: '#b91c1c' }}>
          <div><i className="fas fa-triangle-exclamation" /> {error}</div>
          <button className="btn" style={{ marginTop: 8 }} onClick={() => fetchSessions(page)}>Retry</button>
        </div>
      )}
      <div className="session-list">
        {sessions.map((s) => (
          <SessionListItem
            key={s.session_id}
            session={s}
            projectId={projectId}
            isActive={s.session_id === sessionId}
            onRenamed={handleSessionRenamed}
          />
        ))}
        {!loading && !error && sessions.length === 0 && (
          <div style={{ padding: '12px', color: '#6b7280' }}>No sessions yet</div>
        )}
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px', marginTop: 8 }}>
          <button className="btn" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><i className="fas fa-chevron-left" /> Prev</button>
          <div style={{ alignSelf: 'center', fontSize: 12, color: '#6b7280' }}>Page {page} of {totalPages}</div>
          <button className="btn" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next <i className="fas fa-chevron-right" /></button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`sessions-layout`}>
      <ProjectsHeader
        isMenuOpen={isMenuOpen}
        onToggle={() => setIsMenuOpen((v) => {
          const next = !v;
          try { document.cookie = `nav_open=${next ? '1' : '0'}; path=/; max-age=15552000`; } catch (_) {}
          return next;
        })}
        title={headerTitle}
        rightContent={projectId && !isSummaryRoute ? (
          <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-sm ghost"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => navigate(`/p/${projectId}/summary${sessionId ? `?session=${encodeURIComponent(sessionId)}` : ''}`)}
            >
              <i className="fas fa-clipboard-list" /> Project summary
            </button>
            {projectCanEdit && (
              <button
                type="button"
                className="btn btn-sm ghost"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => navigate(`/p/${projectId}/settings${sessionId ? `?session=${encodeURIComponent(sessionId)}` : ''}`)}
              >
                <i className="fas fa-gear" /> Project settings
              </button>
            )}
          </div>
        ) : null}
      />
      <NavigationMenu
        isOpen={isMenuOpen}
        onToggle={() => setIsMenuOpen((v) => {
          const next = !v;
          try { document.cookie = `nav_open=${next ? '1' : '0'}; path=/; max-age=15552000`; } catch (_) {}
          return next;
        })}
        disableTransition={disableAnim}
        linkItems={[
          ...buildMainNavLinks({ user, horizonBadge, embeddingCount, reviewCount, schedulerErrorCount }),
          { id: 'new-chat', label: 'New Chat', icon: 'fas fa-plus', to: projectId ? `/p/${projectId}` : '/projects', hardMatch: true },
        ]}
        extraContent={extraContent}
      />
      <div className="sessions-container">
        <MainContentArea isMenuOpen={isMenuOpen} disableTransition={disableAnim}>
          <div className="sessions-main-content">
            <StaleEmbeddingBanner />
            <Outlet />
          </div>
        </MainContentArea>
      </div>
    </div>
  );
};

export default SessionsLayout;
